import * as AddressFormatter from '@shopify/address/build/ts/index';
import axios from 'axios';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Setting } from '../App';
import { CUSTOMER_API_URL } from '../constants';
import { Country } from '@shopify/address/build/ts/index';
import { Response as getSubscriptionResponse } from '../types/api/getSubscription/response';
import { Request as validateDeliveryAddressRequest } from '../types/api/validateDeliveryAddress/request';
import { validateDeliveryAddress as callValidateDeliveryAddressApi } from '../services/api/validateDeliveryAddress';

type Subscription = getSubscriptionResponse & {
  countries: Country[];
  country: Country;
  disabled: boolean;
};

export const useAddress = (setting: Setting) => {
  const [subscription, setSubscription] = useState<Subscription>();

  // 配送住所の妥当性チェックでエラーがあった場合に表示するエラーメッセージの管理用
  const defaultErrors = {
    postalCode: null,
    prefecture: null,
    city: null,
    address: null,
  };
  const [validationErrors, setValidationErrors] = useState<{
    postalCode: string | null; // 郵便番号
    prefecture: string | null; // 都道府県
    city: string | null; // 市区町村
    address: string | null; // 住所（deliveryAddress1）
  }>(defaultErrors);

  const { id } = useParams<{ id: string }>();
  const reactRouterHistory = useHistory();

  // TODO: locale
  const addressFormatter = new AddressFormatter.default('ja');

  const formRef = useRef(null);
  useEffect(() => {
    axios
      .post(
        `${CUSTOMER_API_URL}?action=getSubscription`,
        {
          subscriptionId: id,
        },
        {
          headers: { Authorization: setting.token },
        }
      )
      .then((response) => {
        setSubscription({
          ...response.data,
          countries: [],
          country: { zones: [] },
          disabled: false,
        });
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (!subscription || subscription.countries.length > 0) return;

      // Ignore if subscription's delivery address is blank
      if (!subscription.deliveryCountryCode) return;

      const countries = await addressFormatter.getCountries();
      const country = await addressFormatter.getCountry(subscription.deliveryCountryCode);

      setSubscription({
        ...subscription,
        countries,
        country,
      });
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  useLayoutEffect(() => {
    // Safari doesn't compute "float: left" height.
    if (!document.querySelector('body')!.scrollHeight) {
      return;
    }

    const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

    setting.resize(height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const redirectToShowPage = () => {
    reactRouterHistory.push(`/subscriptions/${id}`);
  };

  const changeCountry = (countryCode: string) => {
    addressFormatter.getCountry(countryCode).then((country) => {
      setSubscription({
        ...subscription!,
        deliveryCountryCode: countryCode,
        country,
      });

      validateDeliveryAddress({ country, countryCode });
    });
  };

  // 配送住所の妥当性チェック処理
  const [fetchingValidateResult, setFetchingValidateResult] = useState(false);
  const [latestValidateInput, setLatestValidateInput] = useState<validateDeliveryAddressRequest | null>(null);
  const validateDeliveryAddress = async (changedValue?: {
    country?: AddressFormatter.Country;
    countryCode?: string;
    provinceCode?: string;
  }) => {
    // 必要なデータが揃っていない、ストアが配送住所チェック機能の利用条件を満たしていない、チェック処理実行中の場合はスキップ
    if (!subscription || !subscription.shop.availableDeliveryAddressValidation || fetchingValidateResult) return;

    const country = changedValue?.country ?? subscription.country;
    const countryCode = changedValue?.countryCode ?? subscription.deliveryCountryCode;
    const deliveryProvinceCode = changedValue?.provinceCode ?? subscription.deliveryProvinceCode;

    // 都道府県はコード値で管理してるので、住所チェック用に日本語の名称を取得（なければnull）
    const prefecture =
      (deliveryProvinceCode && country.zones.find((z) => z.code === deliveryProvinceCode)?.name) ?? null;

    // 日本の住所で、郵便番号・都道府県・市区町村・住所がすべて入力されている場合に、住所チェックを行う
    if (
      countryCode !== 'JP' ||
      !subscription.deliveryZip ||
      !prefecture ||
      !subscription.deliveryCity ||
      !subscription.deliveryAddress1
    ) {
      setValidationErrors(defaultErrors);
      setLatestValidateInput(null);

      return;
    }

    // 前回チェック時と値が変わってなければスキップ
    if (
      latestValidateInput?.postalCode === subscription.deliveryZip &&
      latestValidateInput?.prefecture === prefecture &&
      latestValidateInput?.city === subscription.deliveryCity &&
      latestValidateInput?.address === subscription.deliveryAddress1
    ) {
      return;
    }

    setFetchingValidateResult(true);
    const request: validateDeliveryAddressRequest = {
      postalCode: subscription.deliveryZip,
      prefecture,
      city: subscription.deliveryCity,
      address: subscription.deliveryAddress1,
    };
    setLatestValidateInput(request);
    const response = await callValidateDeliveryAddressApi(setting.token, request);
    setFetchingValidateResult(false);

    // 何らかのAPIエラー発生時、あるいはチェック結果に問題がなければ何もしない
    if ('error' in response || !response.data.hasError) {
      if ('error' in response && response.error.status === 400) {
        // 無効な郵便番号（アルファベット混じりなど）があるとここに入るので、その時はエラーメッセージを出す
        // FIXME: フォームバリデーションで郵便番号チェックをするようになったら、この分岐が不要になるので消す
        setValidationErrors({
          ...defaultErrors,
          postalCode: '郵便番号が無効です',
        });
      } else {
        setValidationErrors(defaultErrors);
      }
      return;
    }

    setValidationErrors({
      ...defaultErrors,
      ...response.data.errors.reduce((prev, e) => {
        switch (e.field) {
          case 'postalCode':
            return { ...prev, postalCode: '存在しない郵便番号が入力されています' };
          case 'prefecture':
            return { ...prev, prefecture: `"${e.candidateValues!.join(', ')}" ではありませんか？` };
          case 'city':
            return { ...prev, city: `"${e.candidateValues!.join(', ')}" ではありませんか？` };
          case 'address':
            return { ...prev, address: `"${e.candidateValues!.join(', ')}" ではありませんか？` };
        }
        return prev;
      }, {}),
    });
  };

  const submit = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    if (!(formRef as any).current.checkValidity()) {
      alert('必須入力箇所が空欄です');
      return;
    }

    setSubscription({
      ...subscription!,
      disabled: true,
    });

    const {
      deliveryAddress1,
      deliveryAddress2,
      deliveryCity,
      deliveryCompany,
      deliveryCountryCode,
      deliveryFirstName,
      deliveryLastName,
      deliveryPhone,
      deliveryProvinceCode,
      deliveryZip,
    } = subscription!;

    try {
      await axios.post(
        `${CUSTOMER_API_URL}?action=updateDeliveryAddress`,
        {
          subscriptionId: id,
          deliveryAddress1,
          deliveryAddress2,
          deliveryCity,
          deliveryCompany,
          deliveryCountryCode,
          deliveryFirstName,
          deliveryLastName,
          deliveryPhone,
          deliveryProvinceCode,
          deliveryZip,
        },
        {
          headers: { Authorization: setting.token },
        }
      );
    } catch (e) {
      // TODO
      alert('不正な入力があります');

      return;
    }

    redirectToShowPage();
  };

  return {
    subscription,
    formRef,
    redirectToShowPage,
    setSubscription,
    changeCountry,
    validateDeliveryAddress,
    validationErrors,
    submit,
  };
};
